import Vue from "vue";
import VueResize from "vue-resize";
import VueI18n from "vue-i18n";
import HorizonApp from "./components/HorizonApp.vue";
import { horizonService } from "./stateMachines/horizonStateMachine";
import * as HorizonEvents from "./constants/horizonEvents";
import * as HorizonTasks from "./constants/horizonTasks";
import * as WidgetEvents from "./constants/widgetEvents";

// Horizon helper object providing access to functions on the root Horizon Vue Component

Vue.use(VueResize);
Vue.use(VueI18n);

function Horizon() {
    let horizonApp;
    this.setup = (horizonData) => {
        const hData = JSON.parse(horizonData);
        const htmlElement = document.documentElement;
        htmlElement.setAttribute("theme", hData.theme);
        const i18n = new VueI18n({
            locale: hData.languageCode,
            silentTranslationWarn: true, //* These don't show in production anyway so can silence them here
        });

        horizonService.service.send({
            type: HorizonTasks.UPDATE_DATA,
            horizonData: hData,
        });

        // Create the Horizon Vue App and mount it in the Horizon page
        const HorizonConstructor = Vue.extend(HorizonApp);

        horizonApp = new HorizonConstructor({ i18n }).$mount("#horizonApp");
    };
    this.updateHorizonData = (horizonData) => {
        horizonService.service.send({
            type: HorizonTasks.UPDATE_DATA,
            horizonData: JSON.parse(horizonData),
        });
    };
    // Public functions used to communicate between the "Horizon" horizonApp Vue instance
    this.loadWidget = (widgetId, widgetName, containerData) => {
        horizonService.service.send({
            type: HorizonEvents.QUEUE_ADD,
            task: {
                type: HorizonTasks.WIDGET_ADD,
                data: {
                    widgetId,
                    widgetName,
                    containerData,
                },
            },
        });
    };
    this.showModal = (modalData) => {
        horizonService.service.send({
            type: HorizonEvents.QUEUE_ADD,
            task: {
                type: HorizonTasks.SHOW_MODAL,
                data: modalData,
            },
        });
    };
    this.showDialog = (id) => {
        const dialogElement = document.getElementById(id);
        if (dialogElement !== null && !dialogElement.open) {
            dialogElement.showModal();
        }
    };
    this.hideDialog = (id) => {
        const dialogElement = document.getElementById(id);
        if (dialogElement !== null) {
            dialogElement.close();
        }
    };
    this.hideModal = () => {
        // Remote call to hide current modal - trust whatever calls this to handle the impacts
        horizonApp.handleModalClosed();
    };
    this.widgetAscxHasLoaded = (widgetId, data) => {
        horizonService.sendToWidget(widgetId, {
            type: WidgetEvents.WIDGET_ASCX_LOADED,
            data,
        });
    };
    this.horizonTriggeredUpdate = (widgetId) => {
        horizonService.sendToWidget(
            widgetId,
            WidgetEvents.UPDATE_REQUEST_FROM_HORIZON
        );
    };
    this.widgetNoLongerMaximised = (widgetId) => {
        horizonService.sendToWidget(
            widgetId,
            WidgetEvents.DISPLAY_ANOTHER_WIDGET_WAS_MAXIMISED
        );
    };
    this.overLayTriggered = (isOverlayShowing) => {
        horizonApp.overLayTriggered(isOverlayShowing);
    };
    this.forceReloadAllWidgets = () => {
        horizonApp.forceReloadAllWidgets();
    };
    this.raiseEvent = (eventDetails) => {
        horizonApp.raiseEvent(eventDetails);
    };

    // helper functions to monitor when Horizon is showing various overlays
    // Some use visibility: hidden, some use display: none which is why we need one of each
    this.respondToVisibility = (selector, callback, removeCondition) => {
        const options = {
            root: document.documentElement,
        };
        const element = document.querySelector(selector);

        const observer = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                const isVisible = entry.intersectionRatio > 0;
                if (isVisible === removeCondition) {
                    observer.unobserve(entry.target);
                }
                callback(isVisible);
            });
        }, options);
        observer.observe(element);
    };
    this.addVisibilityObserver = (selector, removeIf) => {
        this.respondToVisibility(
            selector,
            (visible) => {
                this.overLayTriggered(visible);
            },
            removeIf
        );
    };
    this.respondToDisplayNone = (selector, callback) => {
        const element = document.querySelector(selector);
        const observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                if (mutation.attributeName === "style") {
                    callback(
                        window
                            .getComputedStyle(element)
                            .getPropertyValue("display") !== "none"
                    );
                }
            });
        });
        observer.observe(element, { attributes: true });
    };
    this.addDisplayNoneObserver = (selector) => {
        this.respondToDisplayNone(selector, this.overLayTriggered);
    };
}

export default {
    Horizon,
};
